import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetApiIntegrationsWebosTvs,
  usePostApiIntegrationsWebosTvs,
  usePutApiIntegrationsWebosTvsId,
  useDeleteApiIntegrationsWebosTvsId,
  usePostApiIntegrationsWebosTvsIdPause,
  usePostApiIntegrationsWebosTvsIdResume,
  getGetApiIntegrationsWebosTvsQueryKey,
  type WebosTV,
  type WebosTVConnectionState,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft, Pause, Pencil, Play, Plus, Trash2, Tv } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button, Modal, TextField } from '@/components/ui';

const addTVSchema = z.object({
  humanId: z
    .string()
    .min(1, 'ID is required')
    .regex(/^[a-z0-9.-]+$/, 'ID must be lowercase letters, numbers, hyphens, and periods only'),
  name: z.string().min(1, 'Name is required'),
  host: z.string().min(1, 'IP address is required').ip({ message: 'Must be a valid IP address' }),
});

const editTVSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  host: z.string().min(1, 'IP address is required').ip({ message: 'Must be a valid IP address' }),
});

type AddTVFormData = z.infer<typeof addTVSchema>;
type EditTVFormData = z.infer<typeof editTVSchema>;

export function WebosSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useGetApiIntegrationsWebosTvs();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTV, setEditingTV] = useState<WebosTV | null>(null);
  const [deletingTV, setDeletingTV] = useState<WebosTV | null>(null);
  const [addError, setAddError] = useState<string | null>(null);

  const addMutation = usePostApiIntegrationsWebosTvs();
  const updateMutation = usePutApiIntegrationsWebosTvsId();
  const deleteMutation = useDeleteApiIntegrationsWebosTvsId();
  const pauseMutation = usePostApiIntegrationsWebosTvsIdPause();
  const resumeMutation = usePostApiIntegrationsWebosTvsIdResume();

  const tvs = data?.tvs ?? [];

  const invalidateTVs = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsWebosTvsQueryKey() });
  };

  const handlePauseResume = async (tv: WebosTV) => {
    if (!tv.id) return;
    try {
      if (tv.paused) {
        await resumeMutation.mutateAsync({ id: tv.id });
      } else {
        await pauseMutation.mutateAsync({ id: tv.id });
      }
      invalidateTVs();
    } catch {
      // Error handling done by mutation
    }
  };

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              webOS TVs
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage your LG webOS TV connections
            </p>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
            Add TV
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch TVs</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your TV list.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* TV List */}
      {!isLoading && !isError && (
        <>
          {tvs.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Tv className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No TVs configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                Add your first webOS TV to get started
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Add TV
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Configured TVs
              </h2>
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
                {tvs.map((tv, index) => (
                  <TVListItem
                    key={tv.id}
                    tv={tv}
                    isFirst={index === 0}
                    onEdit={() => setEditingTV(tv)}
                    onDelete={() => setDeletingTV(tv)}
                    onPauseResume={() => handlePauseResume(tv)}
                    isPauseLoading={
                      (pauseMutation.isPending || resumeMutation.isPending) &&
                      (pauseMutation.variables?.id === tv.id ||
                        resumeMutation.variables?.id === tv.id)
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Add TV Modal */}
      <AddTVModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setAddError(null);
        }}
        onSubmit={async (data) => {
          setAddError(null);
          try {
            await addMutation.mutateAsync({ data });
            setShowAddModal(false);
            invalidateTVs();
          } catch (err) {
            const error = err as { code?: string; message?: string };
            if (error.code === 'REGISTRATION_NOT_APPROVED') {
              setAddError(
                'TV registration was not approved. Please accept the pairing request on your TV and try again.'
              );
            } else if (error.code === 'TV_UNREACHABLE') {
              setAddError(
                'Could not connect to the TV. Please verify the IP address is correct and the TV is turned on.'
              );
            } else if (error.code === 'TV_ALREADY_EXISTS') {
              setAddError(error.message ?? 'A TV with this configuration already exists.');
            } else {
              setAddError('Failed to add TV. Please try again.');
            }
          }
        }}
        isLoading={addMutation.isPending}
        error={addError}
      />

      {/* Edit TV Modal */}
      {editingTV && (
        <EditTVModal
          isOpen={true}
          tv={editingTV}
          onClose={() => setEditingTV(null)}
          onSubmit={async (data) => {
            if (!editingTV.id) return;
            try {
              await updateMutation.mutateAsync({ id: editingTV.id, data });
              setEditingTV(null);
              invalidateTVs();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingTV && (
        <DeleteTVModal
          isOpen={true}
          tv={deletingTV}
          onClose={() => setDeletingTV(null)}
          onConfirm={async () => {
            if (!deletingTV.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingTV.id });
              setDeletingTV(null);
              invalidateTVs();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

interface TVListItemProps {
  tv: WebosTV;
  isFirst: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onPauseResume: () => void;
  isPauseLoading: boolean;
}

function TVListItem({
  tv,
  isFirst,
  onEdit,
  onDelete,
  onPauseResume,
  isPauseLoading,
}: TVListItemProps) {
  const connectionState = tv.connection?.state as WebosTVConnectionState | undefined;

  const getStatusColor = () => {
    if (tv.paused) return 'bg-gray-400 dark:bg-zinc-600';
    switch (connectionState) {
      case 'connected':
        return 'bg-emerald-400 shadow-lg shadow-emerald-400/30';
      case 'connecting':
        return 'bg-amber-400 shadow-lg shadow-amber-400/30';
      case 'disconnected':
        return 'bg-gray-400 dark:bg-zinc-600';
      case 'unreachable':
        return 'bg-red-400 shadow-lg shadow-red-400/30';
      default:
        return 'bg-gray-400 dark:bg-zinc-600';
    }
  };

  const getStatusText = () => {
    if (tv.paused) return 'Paused';
    switch (connectionState) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting...';
      case 'disconnected':
        return 'Disconnected';
      case 'unreachable':
        return 'Unreachable';
      default:
        return 'Unknown';
    }
  };

  return (
    <div
      className={`flex items-center justify-between p-4 ${
        !isFirst ? 'border-t border-gray-200 dark:border-white/[0.06]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`h-3 w-3 rounded-full ${getStatusColor()}`} />
        <div>
          <div className="text-[15px] font-medium text-gray-900 dark:text-white">{tv.name}</div>
          <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
            <span>{tv.host}</span>
            <span className="text-gray-300 dark:text-zinc-700">•</span>
            <span>{getStatusText()}</span>
            {tv.humanId && (
              <>
                <span className="text-gray-300 dark:text-zinc-700">•</span>
                <span className="font-mono text-xs">{tv.humanId}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPauseResume}
          disabled={isPauseLoading}
          title={tv.paused ? 'Resume monitoring' : 'Pause monitoring'}
        >
          {isPauseLoading ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
          ) : tv.paused ? (
            <Play className="h-4 w-4" />
          ) : (
            <Pause className="h-4 w-4" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
}

interface AddTVModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddTVFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function AddTVModal({ isOpen, onClose, onSubmit, isLoading, error }: AddTVModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddTVFormData>({
    resolver: zodResolver(addTVSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="add-tv-form" loading={isLoading}>
        Add TV
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add webOS TV"
      subtitle="Connect a new LG TV to your smart home"
      size="md"
      footer={footerContent}
    >
      <form id="add-tv-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room TV"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="IP Address"
          placeholder="192.168.1.100"
          error={errors.host?.message}
          {...register('host')}
        />

        <TextField
          label="ID"
          placeholder="living-room-tv"
          error={errors.humanId?.message}
          {...register('humanId')}
        />

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface EditTVModalProps {
  isOpen: boolean;
  tv: WebosTV;
  onClose: () => void;
  onSubmit: (data: EditTVFormData) => Promise<void>;
  isLoading: boolean;
}

function EditTVModal({ isOpen, tv, onClose, onSubmit, isLoading }: EditTVModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditTVFormData>({
    resolver: zodResolver(editTVSchema),
    defaultValues: {
      name: tv.name ?? '',
      host: tv.host ?? '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-tv-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit TV"
      subtitle="Update TV connection settings"
      size="md"
      footer={footerContent}
    >
      <form id="edit-tv-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="Name"
          placeholder="Living Room TV"
          error={errors.name?.message}
          {...register('name')}
        />

        <TextField
          label="IP Address"
          placeholder="192.168.1.100"
          error={errors.host?.message}
          {...register('host')}
        />

        {tv.humanId && (
          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
              ID
            </label>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 font-mono text-sm text-gray-500 dark:border-white/[0.08] dark:bg-[#111113] dark:text-zinc-500">
              {tv.humanId}
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}

interface DeleteTVModalProps {
  isOpen: boolean;
  tv: WebosTV;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteTVModal({ isOpen, tv, onClose, onConfirm, isLoading }: DeleteTVModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete TV"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete{' '}
        <strong className="text-gray-900 dark:text-white">{tv.name}</strong>?
      </p>
    </Modal>
  );
}
